export interface ApiSuccess<T> {
  success: true;
  data: T;
  message: string | null;
}

export interface ApiError {
  success: false;
  error: { code: string; message: string };
}

export class ApiException extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiException';
  }
}

// sessionStorage statt localStorage: ein geteiltes Tablet im Betrieb
// soll nach dem Schliessen abgemeldet sein.
const ACCESS_KEY = 'pf_access';
const REFRESH_KEY = 'pf_refresh';

export const tokenStore = {
  get access(): string | null {
    return sessionStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return sessionStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string): void {
    sessionStorage.setItem(ACCESS_KEY, access);
    sessionStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  },
};

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  const token = tokenStore.refresh;
  if (!token) return false;

  refreshing = fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: token }),
  })
    .then(async (r) => {
      if (!r.ok) return false;
      const body = (await r.json()) as ApiSuccess<{ accessToken: string; refreshToken: string }>;
      tokenStore.set(body.data.accessToken, body.data.refreshToken);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  auth?: boolean;
  retryOn401?: boolean;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, auth = true, retryOn401 = true, ...init } = opts;

  const headers = new Headers(init.headers);
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (auth && tokenStore.access) {
    headers.set('Authorization', `Bearer ${tokenStore.access}`);
  }

  const res = await fetch(path, {
    ...init,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Access-Token abgelaufen -> einmal erneuern, dann wiederholen.
  if (res.status === 401 && auth && retryOn401) {
    if (await tryRefresh()) {
      return api<T>(path, { ...opts, retryOn401: false });
    }
    tokenStore.clear();
    window.dispatchEvent(new CustomEvent('pf:session-expired'));
    throw new ApiException('Sitzung abgelaufen. Bitte neu anmelden.', 'UNAUTHORIZED', 401);
  }

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // Kein JSON (z.B. 502 vom Proxy)
  }

  if (!res.ok) {
    const err = payload as ApiError | null;
    throw new ApiException(
      err?.error?.message ?? 'Es ist ein Fehler aufgetreten. Bitte erneut versuchen.',
      err?.error?.code ?? 'UNKNOWN',
      res.status,
    );
  }

  // Defensiv: Konvention ist {success, data}, aber nie blind darauf verlassen.
  const ok = payload as ApiSuccess<T> | null;
  return (ok?.data ?? (payload as T)) as T;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}
